'use client';

import {
  type AdminV2LoginPopupSceneSlug,
  isAdminV2LoginPopupSceneSlug,
} from '@/site-runtime/code-owned/login-popup-scenes';
import {
  SITE_RUNTIME_WORKSPACE_AUTH_INTENT_DOCUMENT_ATTRIBUTE,
  SITE_RUNTIME_WORKSPACE_AUTH_INTENT_ENDPOINT,
  SITE_RUNTIME_WORKSPACE_AUTH_INTENT_TYPE,
  SITE_RUNTIME_WORKSPACE_AUTH_INTENT_VERSION,
  type SiteRuntimeWorkspaceAuthIntent,
} from './site-runtime-workspace-auth-intent-contract';

type WorkspaceAuthIntentResponse = {
  success?: unknown;
  intent?: unknown;
};

function canUseBrowserRuntime() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function toSameOriginPath(value: string) {
  const url = new URL(value, window.location.origin);
  if (url.origin !== window.location.origin) {
    throw new Error('Workspace auth return URL must use the current origin.');
  }

  return `${url.pathname}${url.search}`;
}

function getCurrentReturnPath() {
  return `${window.location.pathname}${window.location.search}`;
}

function isWorkspaceAuthIntent(
  value: unknown
): value is SiteRuntimeWorkspaceAuthIntent {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<SiteRuntimeWorkspaceAuthIntent>;
  return (
    candidate.version === SITE_RUNTIME_WORKSPACE_AUTH_INTENT_VERSION &&
    candidate.type === SITE_RUNTIME_WORKSPACE_AUTH_INTENT_TYPE &&
    typeof candidate.returnPath === 'string' &&
    candidate.returnPath.startsWith('/') &&
    typeof candidate.pageId === 'string' &&
    candidate.pageId.length > 0 &&
    typeof candidate.boundToolId === 'string' &&
    candidate.boundToolId.length > 0 &&
    isAdminV2LoginPopupSceneSlug(candidate.loginScene) &&
    typeof candidate.createdAt === 'number' &&
    Number.isFinite(candidate.createdAt) &&
    typeof candidate.expiresAt === 'number' &&
    Number.isFinite(candidate.expiresAt)
  );
}

async function requestWorkspaceAuthIntent(
  method: 'DELETE' | 'POST' | 'PUT',
  requestBody?: {
    loginScene?: AdminV2LoginPopupSceneSlug;
    returnPath: string;
  }
) {
  const response = await fetch(SITE_RUNTIME_WORKSPACE_AUTH_INTENT_ENDPOINT, {
    method,
    cache: 'no-store',
    credentials: 'same-origin',
    headers:
      requestBody === undefined
        ? undefined
        : {
            'Content-Type': 'application/json',
          },
    body: requestBody === undefined ? undefined : JSON.stringify(requestBody),
  });
  const payload = (await response
    .json()
    .catch(() => null)) as WorkspaceAuthIntentResponse | null;

  return {
    ok: response.ok && payload?.success === true,
    payload,
  };
}

export function hasSiteRuntimeWorkspaceAuthIntentDocumentHint() {
  return (
    canUseBrowserRuntime() &&
    document.documentElement.hasAttribute(
      SITE_RUNTIME_WORKSPACE_AUTH_INTENT_DOCUMENT_ATTRIBUTE
    )
  );
}

export function showSiteRuntimeWorkspaceAuthIntentDocumentHint() {
  if (!canUseBrowserRuntime()) {
    return;
  }

  document.documentElement.setAttribute(
    SITE_RUNTIME_WORKSPACE_AUTH_INTENT_DOCUMENT_ATTRIBUTE,
    'true'
  );
}

export function clearSiteRuntimeWorkspaceAuthIntentDocumentHint() {
  if (!canUseBrowserRuntime()) {
    return;
  }

  document.documentElement.removeAttribute(
    SITE_RUNTIME_WORKSPACE_AUTH_INTENT_DOCUMENT_ATTRIBUTE
  );
}

export async function issueSiteRuntimeWorkspaceAuthIntent(
  callbackUrl: string,
  loginScene: AdminV2LoginPopupSceneSlug
) {
  if (!canUseBrowserRuntime()) {
    return false;
  }

  try {
    const returnPath = toSameOriginPath(callbackUrl);
    const result = await requestWorkspaceAuthIntent('POST', {
      loginScene,
      returnPath,
    });
    return result.ok;
  } catch {
    return false;
  }
}

export async function consumeSiteRuntimeWorkspaceAuthIntent() {
  if (!canUseBrowserRuntime()) {
    return null;
  }

  try {
    const currentPath = getCurrentReturnPath();
    const result = await requestWorkspaceAuthIntent('PUT', {
      returnPath: currentPath,
    });
    const intent = result.payload?.intent;
    if (
      !result.ok ||
      !isWorkspaceAuthIntent(intent) ||
      toSameOriginPath(intent.returnPath) !== currentPath
    ) {
      clearSiteRuntimeWorkspaceAuthIntentDocumentHint();
      return null;
    }

    return intent;
  } catch {
    clearSiteRuntimeWorkspaceAuthIntentDocumentHint();
    return null;
  }
}

export async function cancelSiteRuntimeWorkspaceAuthIntent() {
  if (!canUseBrowserRuntime()) {
    return;
  }

  try {
    await requestWorkspaceAuthIntent('DELETE');
  } catch {
    // Local cancellation must settle even when best-effort server cleanup
    // cannot run. The signed intent remains short-lived and revalidated.
  } finally {
    clearSiteRuntimeWorkspaceAuthIntentDocumentHint();
  }
}
